/**
 * The bag — Lorcana's replacement for a stack (CR §7.7, SPEC.md §4.1, §4.3).
 *
 * Only **triggered abilities** ever go in the bag. Whenever a trigger's condition is met the
 * ability is added by the player whose card generated it; nothing resolves until the current
 * effect finishes and the game state check cascade goes quiet (CR §7.7.3.1, §1.8.2). Then the
 * bag resolves: the active player picks any one of *their own* triggers and resolves it fully,
 * keeps picking while they have any, and only then does the bag pass to the next player in turn
 * order (CR §7.7.4). **Players order their own triggers only** — you never order an opponent's,
 * which is why the ordering choice is per player and only surfaces when that player has two or
 * more waiting.
 *
 * ## What ticket 08 changed
 *
 * The trigger representation ticket 07 stubbed here is now the real ability IR
 * (`abilities/ir.ts`): `TriggerCondition` grew the window/guard/limit slots, and `EffectStep`
 * is an alias of the full `Effect` tree. Everything else in this module — collection, the
 * holder rotation, `nextBagHolder`, `takeBagItem` — is untouched, exactly as ticket 07
 * predicted. **Resolving** an ability moved out, to `abilities/interpret.ts`, because an
 * effect can now stop mid-way on a player choice and must live on the resolution stack.
 *
 * Everything here is plain data (SPEC.md §4.2): it clones, serializes and compares like the
 * rest of `GameState`, and the resolution of a bag item lives on the explicit resolution stack,
 * never in a suspended JS call frame.
 */
import {
  baseContext,
  evalCondition,
  eventAmountOf,
  eventDefenderOf,
  eventSourceOf,
  matchesSelector,
} from './abilities/selectors.ts';
import type { Duration, Effect, TriggerCondition } from './abilities/ir.ts';
import { cloneIr } from './abilities/ir.ts';
import type { GameEvent } from './events.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';
import type { DurationBoundary } from './modifiers.ts';
import type { GameState, TargetBinding } from './state.ts';
import { instanceAt, turnOrderFrom } from './state.ts';
import { emit } from './zones.ts';

export type {
  Amount,
  CardRef,
  CardTarget,
  Effect,
  EffectStep,
  PlayerRef,
  PlayerTarget,
  Selector,
  TriggerCondition,
} from './abilities/ir.ts';

// ---------------------------------------------------------------------------
// Trigger sources and the abilities that come out of the bag
// ---------------------------------------------------------------------------

/**
 * A triggered ability that can fire. Registered against a card, either because the card
 * entered play (`abilities/interpret.ts`) or because a resolved effect created a floating or
 * delayed triggered ability (CR §6.2.7).
 *
 * Registrations are **not** removed when the source leaves play — a "when this is banished"
 * ability must still fire from the discard. Scoping a trigger to its source being in play is
 * `TriggerCondition.where`'s job.
 */
export interface TriggerSource {
  readonly id: number;
  /** The card whose ability this is. */
  readonly source: InstanceId;
  readonly when: TriggerCondition;
  readonly effect: readonly Effect[];
  /** Debug/UI label — the printed ability name, where the card has one. */
  readonly label: string;
  /**
   * How long a **delayed** triggered ability lasts (CR §6.2.7): "whenever one of your
   * characters quests *this turn*, …". Absent — the ordinary case — is a printed ability,
   * which lives as long as its registration does.
   */
  readonly expires?: Duration;
  /** `state.turn` when it was registered, for the turn-boundary durations. */
  readonly created: number;
  /**
   * Bindings closed over from the frame that registered a **delayed** trigger (ticket 106) —
   * `bound(id)` names in `effect` that must keep resolving once the trigger fires as a fresh
   * `AbilityInstance`, long after the registering frame is gone. `delayedTrigger`'s `closeOver`
   * is what names them; an ordinary printed trigger never carries any.
   */
  readonly bindings?: readonly TargetBinding[];
}

/**
 * The two participants of the challenge a triggering event arose from — carried by instance
 * id, as plain data, because the challenge frame is long gone by the time the ability
 * resolves out of the bag.
 */
export interface ChallengeParticipants {
  readonly challenger: InstanceId;
  readonly defender: InstanceId;
}

/**
 * One ability being resolved. A triggered ability arrives here from the bag; an **activated**
 * ability is pushed straight onto the resolution stack and never enters the bag at all
 * (CR §7.7), which is the only difference between them once resolution starts.
 */
export interface AbilityInstance {
  readonly id: number;
  /** Who resolves it and makes its choices — the controller at trigger/activation time. */
  readonly controller: PlayerIndex;
  readonly source: InstanceId;
  /** The card the triggering event was about, if any — what `{ kind: 'subject' }` resolves to. */
  readonly subject: InstanceId | null;
  /**
   * The card that **caused** the triggering event, if any — what `{ kind: 'event-source' }`
   * resolves to. Recorded here for the same reason as `subject`: the effect resolves out of
   * the bag, long after the event that fed it is gone from `state.events`.
   */
  readonly eventSource?: InstanceId | null;
  /**
   * The triggering event's numeric value — its `amount`, or `card-played.cost` — what
   * `{ kind: 'triggering-amount' }` resolves to. Recorded for the same reason as `eventSource`.
   */
  readonly eventAmount?: number | null;
  /**
   * The defender of the triggering `challenge-declared`, if any — what `{ kind: 'event-defender'
   * }` resolves to (ticket 138). Recorded here for the same reason as `eventSource`: the
   * challenge frame is long gone by the time an effect resolves out of the bag, so a
   * challenger-side trigger that wants to act on the character it challenged ("banish that
   * character") needs its own copy, carried the same route.
   */
  readonly eventDefender?: InstanceId | null;
  readonly effect: readonly Effect[];
  readonly label: string;
  /**
   * How the ability got here. `action` is an Action card's effect (CR §5.4.1.2) — it resolves
   * immediately on play and the card is discarded once it finishes.
   */
  readonly origin: 'triggered' | 'activated' | 'action';
  /**
   * Bindings the effect frame **starts** with — cards the event named rather than a player
   * chose (`eventBindings`). The one case today is a sung song's `singers`.
   */
  readonly bindings?: readonly TargetBinding[];
  /**
   * The challenge the triggering event arose from, if any. An ability set off by a challenge
   * banish resolves "during that challenge" (CR §4.6.6, the cards 71/50 clarification): if it
   * banishes one of these two participants, `zones.ts`'s `banish` attributes that exactly as
   * an in-challenge banish — same `challengeRole`, same `banished-in-challenge` event — so
   * further triggers chain naturally. A banish of anyone else carries no attribution.
   */
  readonly challenge?: ChallengeParticipants;
  /**
   * Where this **Action** goes once its own effect finishes, if not the ordinary discard
   * (CR §5.4.1.2) — "if you do, put that card on the bottom of your deck instead of into your
   * discard" (Ursula - Deceiver of All's WHAT A DEAL, ticket 127). Only ever set on an
   * `origin: 'action'` instance created by a play-for-free effect (`rules.ts`'s `enterPlayFrom`);
   * `engine.ts`'s `stepResolveAbility` reads it in place of the default discard once the
   * effect has finished, per the card's own clarification.
   */
  readonly finishTo?: 'bottom-of-deck';
}

/** One triggered ability waiting in the bag: a `TriggerSource` bound to what set it off. */
export interface BagItem extends AbilityInstance {
  /** The `TriggerSource` this came from. */
  readonly trigger: number;
  readonly origin: 'triggered';
}

/** The bag zone plus the bookkeeping that feeds it. One field on `GameState`. */
export interface BagState {
  /** Triggered abilities that can fire. */
  triggers: TriggerSource[];
  /** Waiting triggered abilities, oldest first. The bag is a public zone (CR §7.7.1). */
  items: BagItem[];
  /** Next id for a trigger source or a bag item. Ids are dense and never reused. */
  nextId: number;
  /** How far into `state.events` triggers have been collected. */
  eventCursor: number;
}

export function createBagState(): BagState {
  return { triggers: [], items: [], nextId: 0, eventCursor: 0 };
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/** What `registerTrigger` needs; the id is the engine's to assign. */
export interface TriggerSpec {
  readonly source: InstanceId;
  readonly when: TriggerCondition;
  readonly effect: readonly Effect[];
  readonly label?: string;
  /** Set for a delayed/floating ability; `expireTriggers` drops it at that boundary. */
  readonly expires?: Duration;
  /** See `TriggerSource.bindings` — closed over from the registering frame (ticket 106). */
  readonly bindings?: readonly TargetBinding[];
}

/**
 * Register a triggered ability. `abilities/interpret.ts` registers a card's triggered
 * abilities as it enters play, and its floating and delayed triggered abilities as the
 * effects that create them resolve (CR §6.2.7).
 */
export function registerTrigger(state: GameState, spec: TriggerSpec): TriggerSource {
  const trigger: TriggerSource = {
    id: state.bag.nextId,
    source: spec.source,
    when: cloneIr(spec.when),
    effect: cloneIr(spec.effect) as Effect[],
    label: spec.label ?? '',
    ...(spec.expires === undefined ? {} : { expires: spec.expires }),
    ...(spec.bindings === undefined || spec.bindings.length === 0
      ? {}
      : { bindings: spec.bindings.map((each) => ({ id: each.id, instances: [...each.instances] })) }),
    created: state.turn,
  };
  state.bag.nextId += 1;
  state.bag.triggers.push(trigger);
  return trigger;
}

/**
 * Drop the **delayed** triggered abilities whose duration has run out — the trigger-side twin
 * of `expireModifiers`, called at the same two boundaries. A printed ability carries no
 * `expires` and is never touched here.
 */
export function expireTriggers(state: GameState, boundary: DurationBoundary): void {
  state.bag.triggers = state.bag.triggers.filter((trigger) => {
    if (trigger.expires === undefined || trigger.expires === 'permanent') return true;
    if (trigger.expires === 'this-turn') return boundary !== 'end-of-turn';
    return !(
      boundary === 'start-of-turn' &&
      state.activePlayer === instanceAt(state, trigger.source).controller &&
      state.turn > trigger.created
    );
  });
}

/** Drop a registered trigger — how a floating ability expires. */
export function unregisterTrigger(state: GameState, id: number): void {
  const at = state.bag.triggers.findIndex((each) => each.id === id);
  if (at !== -1) state.bag.triggers.splice(at, 1);
}

/** Drop every trigger registered against one card — how a card's abilities are re-registered. */
export function unregisterTriggersFrom(state: GameState, source: InstanceId): void {
  state.bag.triggers = state.bag.triggers.filter((each) => each.source !== source);
}

// ---------------------------------------------------------------------------
// Collecting triggers
// ---------------------------------------------------------------------------

/** A trigger that fires on its own arrival would spin forever; this is the tripwire. */
const MAX_TRIGGER_COLLECTION = 4096;

/**
 * Add a bag item for every trigger whose condition was met by an event emitted since the last
 * call (CR §7.7.2). Triggers enter the bag the moment their condition is met, but resolve only
 * once the current effect and the game state check cascade are finished (CR §1.8.2, §7.7.3.1) —
 * which is why collection is separate from resolution and the engine drives them in that order.
 */
export function collectTriggers(state: GameState): void {
  const bag = state.bag;
  let guard = 0;
  while (bag.eventCursor < state.events.length) {
    guard += 1;
    if (guard > MAX_TRIGGER_COLLECTION) {
      throw new Error('Trigger collection did not settle — an ability is triggering itself');
    }
    const event = state.events[bag.eventCursor];
    bag.eventCursor += 1;
    if (event === undefined) continue;
    // Snapshot: a trigger registered *by* this event's own triggers must not see it.
    for (const trigger of [...bag.triggers]) {
      if (triggerMatches(state, trigger, event)) addToBag(state, trigger, event);
    }
  }
}

function addToBag(state: GameState, trigger: TriggerSource, event: GameEvent): void {
  if (trigger.when.batch === true && mergeBatchedTrigger(state, trigger)) return;
  const source = instanceAt(state, trigger.source);
  const item: BagItem = {
    id: state.bag.nextId,
    trigger: trigger.id,
    controller: source.controller,
    source: trigger.source,
    subject: eventInstance(event),
    eventSource: eventSourceOf(event),
    eventAmount: trigger.when.batch === true ? 1 : eventAmountOf(event),
    eventDefender: eventDefenderOf(event),
    effect: cloneIr(trigger.effect) as Effect[],
    label: trigger.label,
    origin: 'triggered',
    ...bindingsFor(trigger, event),
    ...eventChallenge(event),
  };
  state.bag.nextId += 1;
  state.bag.items.push(item);
  if (trigger.when.limit !== undefined) state.abilityUses.push(triggerUseKey(trigger.id));
  emit(state, {
    type: 'ability-triggered',
    player: item.controller,
    source: item.source,
    defId: source.defId,
    item: item.id,
    ability: item.label,
  });
}

/**
 * `TriggerCondition.batch`'s merge (ticket 124): if this trigger already has an item sitting
 * unresolved in the bag, fold this match into it — bump `eventAmount` — instead of adding a
 * second one. Returns whether a merge happened; `addToBag` falls through to the ordinary
 * creation path when it did not (the trigger's first match in this action).
 *
 * "Unresolved" is the right test for "still the same action": the bag stays closed for as long
 * as the effect that is discarding cards is still resolving (`gsc.ts`'s `bagWindowOpen`), so an
 * item only leaves `bag.items` once a player has actually taken it to resolve — at which point
 * a further matching event is unambiguously the *next* discard action, and starts a fresh item.
 */
function mergeBatchedTrigger(state: GameState, trigger: TriggerSource): boolean {
  const at = state.bag.items.findIndex((item) => item.trigger === trigger.id);
  const existing = state.bag.items[at];
  if (existing === undefined) return false;
  state.bag.items[at] = { ...existing, eventAmount: (existing.eventAmount ?? 1) + 1 };
  return true;
}

/**
 * Cards the **event** names, bound for the ability it sets off. `song` is "whenever this
 * character sings a song, you may play **that song** again" (Ursula - Deceiver of All, ticket
 * 127) — the one card no selector could re-find once the singing is over, since the trigger's
 * own `subject` already names the singer, not the song. `singers` is the `song-sung` twin:
 * "whenever one or more of your characters sings a song, you may ready those characters" acts
 * on a set that no selector could describe once the singing is over, and that the ability's
 * controller never chose. `under-cards` is the same shape for a banish's pre-dissolve pile
 * (ticket 104) — "for each card that was under him" (`boundCount('under-cards')`) and "put all
 * cards that were under him under another chosen character" (`bound('under-cards')`) both need
 * the exact former under-cards, which the board can no longer answer by the time the ability
 * resolves out of the bag.
 *
 * An ability that reads a binding this did not supply simply finds nothing bound, which is the
 * ordinary "do as much as you can" (CR §1.2.3).
 */
function eventBindings(event: GameEvent): { bindings?: TargetBinding[] } {
  if (event.type === 'card-sung') {
    return { bindings: [{ id: 'song', instances: [event.song] }] };
  }
  if (event.type === 'song-sung') {
    return { bindings: [{ id: 'singers', instances: [...event.singers] }] };
  }
  if (event.type === 'card-banished' || event.type === 'banished-in-challenge') {
    return { bindings: [{ id: 'under-cards', instances: [...event.underCards] }] };
  }
  return {};
}

/**
 * A firing bag item's bindings: what the **event** names (`eventBindings`) plus what the
 * **registering frame** closed over (`TriggerSource.bindings`, ticket 106) — "play a character
 * for free … at the end of your turn, banish them" needs `bound('target')` to still name the
 * character once the delayed trigger fires, long after the frame that chose it is gone. The
 * two never collide by id in practice (`closeOver` names the registering ability's own
 * bindings, distinct from what a triggering event names), so this is a plain concatenation.
 */
function bindingsFor(trigger: TriggerSource, event: GameEvent): { bindings?: TargetBinding[] } {
  const carried = trigger.bindings ?? [];
  const fromEvent = eventBindings(event).bindings ?? [];
  if (carried.length === 0 && fromEvent.length === 0) return {};
  return {
    bindings: [
      ...carried.map((each) => ({ id: each.id, instances: [...each.instances] })),
      ...fromEvent,
    ],
  };
}

/**
 * The challenge an event arose from, read off its banish attribution — the two events that
 * carry it are `banished-in-challenge` and a `card-banished` with a `challengeRole`. Riding
 * on the bag item, this is what lets the ability's own banishes count as happening "during
 * that challenge" long after the challenge frame has popped.
 */
function eventChallenge(event: GameEvent): { challenge?: ChallengeParticipants } {
  if (event.type === 'banished-in-challenge') {
    return {
      challenge:
        event.role === 'challenger'
          ? { challenger: event.instance, defender: event.by }
          : { challenger: event.by, defender: event.instance },
    };
  }
  if (event.type === 'card-banished' && event.challengeRole !== null && event.source !== null) {
    return {
      challenge:
        event.challengeRole === 'challenger'
          ? { challenger: event.instance, defender: event.source }
          : { challenger: event.source, defender: event.instance },
    };
  }
  return {};
}

/** The `GameState.abilityUses` key for a per-turn-limited trigger (CR §6.1.13.2). */
export function triggerUseKey(triggerId: number): string {
  return `t${triggerId}`;
}

/**
 * How many times each `TriggerCondition.limit` reading may fire per turn before the trigger
 * stops meeting its condition. One counter (`state.abilityUses`, pushed once per met
 * condition — see `addToBag`) serves every reading: `triggerMatches` below just counts this
 * trigger's own keys and compares against the map, so a new "N times per turn" reading is one
 * more entry here, not new bookkeeping.
 */
const TRIGGER_USE_LIMITS: Record<NonNullable<TriggerCondition['limit']>, number> = {
  'once-per-turn': 1,
  'twice-per-turn': 2,
};

/**
 * Whether one registered trigger's condition is met by one event — the four slots of
 * `TriggerCondition` in order: the event and its subject, where the source must be, the turn
 * window, the per-turn use limit, and finally the state guard.
 */
function triggerMatches(state: GameState, trigger: TriggerSource, event: GameEvent): boolean {
  const when = trigger.when;
  if (event.type !== when.event) return false;

  // The damage taxonomy (CR §1.9): "is dealt damage" is narrower than "takes damage", and a
  // trigger that says so names the verbs it accepts. Moved damage emits no `damage-dealt`
  // event at all, so it can never meet either reading.
  if (when.damageVerb !== undefined) {
    if (event.type !== 'damage-dealt' || !when.damageVerb.includes(event.verb)) return false;
  }

  // "At the start of your turn" names one phase; the event fires at every phase boundary
  // (CR §3.2, §3.3, §3.4), so a phase-scoped trigger has to say which one.
  if (when.phase !== undefined) {
    if (event.type !== 'phase-began' || event.phase !== when.phase) return false;
  }

  const source = instanceAt(state, trigger.source);
  const where = when.where ?? 'anywhere';
  if (where === 'own-shift') {
    // Ticket 80 (Hiro Hamada - Future Champion, 1523): "when you play a Floodborn character on
    // this card" fires from the base's own ability *as* the Shift resolves — the `card-put-under`
    // event that just moved this exact card into `under`, and only that one event. CR §4.3.4.8
    // sets a play-triggered condition as met at the step that causes it, before CR §8.10.4's
    // disablement of a covered card's abilities applies to anything that follows. This is a
    // narrow one-shot exception, not a reopening of the zone gate below: every other event this
    // card might otherwise try to react to once buried still hits the ordinary check.
    if (event.type !== 'card-put-under' || event.instance !== trigger.source) return false;
  } else {
    // A card under another card is not in play and **none** of its abilities function
    // (CR §8.10.4) — not even the ones that would otherwise work from anywhere, because the
    // card is not in a zone whose abilities function at all (CR §6.1.12 lists hand and discard).
    if (source.zone === 'under') return false;
    if (where === 'in-play' && source.zone !== 'play') return false;
    // An ability that functions from the discard does so only while the card is still there
    // (CR §6.1.12) — the printed "if this card is in your discard", read at collection time.
    if (where === 'discard' && source.zone !== 'discard') return false;
  }

  const about = eventInstance(event);
  const controller = source.controller;
  switch (when.subject) {
    case 'any':
      break;
    case 'self':
      if (about !== trigger.source) return false;
      break;
    case 'controller':
      if (!aboutPlayer(state, event, about, (player) => player === controller)) return false;
      break;
    case 'opponent':
      if (!aboutPlayer(state, event, about, (player) => player !== controller)) return false;
      break;
  }

  if (when.filter !== undefined) {
    if (about === null) return false;
    const ctx = baseContext(trigger.source, controller, about, event);
    const card = instanceAt(state, about);
    // A trigger's filter describes the card the event was *about*, wherever that has left it:
    // "whenever one of your characters is banished" is collected with the card already in the
    // discard (CR §7.6), and a selector's zone defaults to `play`. So an unstated zone means
    // "wherever it is", while a filter that names a zone still means exactly that zone.
    const filter = when.filter.zone === undefined ? { ...when.filter, zone: card.zone } : when.filter;
    if (!matchesSelector(state, ctx, card, filter)) return false;
  }

  const window = when.window ?? 'any-turn';
  if (window === 'your-turn' && state.activePlayer !== controller) return false;
  if (window === 'opponent-turn' && state.activePlayer === controller) return false;

  if (when.limit !== undefined) {
    const key = triggerUseKey(trigger.id);
    const used = state.abilityUses.reduce((count, each) => (each === key ? count + 1 : count), 0);
    if (used >= TRIGGER_USE_LIMITS[when.limit]) return false;
  }

  if (when.guard !== undefined) {
    // Same bindings the bag item would carry into its own effect (`bindingsFor`) — a guard is
    // "is this met, given what just happened", and what just happened is exactly what the
    // event named. Ticket 139: `onSing`'s guard reads `bound('song')`'s keywords ("…sings a
    // song with Sing Together"), which needs the event's `song` binding to already be there.
    const ctx = { ...baseContext(trigger.source, controller, about, event), ...bindingsFor(trigger, event) };
    if (!evalCondition(state, ctx, when.guard)) return false;
  }
  return true;
}

/** Whether the player an event is about satisfies a test — via its card, else via its player. */
function aboutPlayer(
  state: GameState,
  event: GameEvent,
  about: InstanceId | null,
  test: (player: PlayerIndex) => boolean,
): boolean {
  if (about !== null) return test(instanceAt(state, about).controller);
  const player = eventPlayer(event);
  return player !== null && test(player);
}

/** Which card an event is about, for `self`/`controller` conditions and `subject` targets. */
export function eventInstance(event: GameEvent): InstanceId | null {
  switch (event.type) {
    case 'card-drawn':
    case 'card-discarded':
    case 'card-inked':
    case 'card-played':
    case 'card-sung':
    // The song that was sung — one event per song, whatever it took to sing it (CR §8.12).
    case 'song-sung':
    case 'character-quested':
    // The character that moved; the location it moved to is the event's `location`.
    case 'character-moved':
    case 'damage-dealt':
    case 'damage-removed':
    // The card the counters moved *to*; the donor's own `damage-removed` names it separately.
    case 'damage-moved':
    case 'card-banished':
    case 'card-returned':
    case 'card-chosen':
    // The card discarded from a hand (CR §7.3) — "whenever you discard a card" and "when you
    // discard this card" (a `where: 'discard'` ability functioning from the pile it just
    // landed in) both read the card this names.
    case 'card-discarded':
    // The card that went under, or came out from under, another one (CR §8.10.4, §8.3).
    case 'card-put-under':
    case 'card-left-stack':
    // The defender of a challenge, the card that left play, the card that was readied, and the
    // card banished in a challenge (tickets 16–19).
    case 'card-challenged':
    case 'card-left-play':
    case 'card-left-discard':
    case 'card-readied':
    case 'character-exerted':
    case 'banished-in-challenge':
      return event.instance;
    case 'challenge-declared':
      return event.challenger;
    case 'ability-triggered':
    case 'ability-resolved':
    case 'ability-activated':
      return event.source;
    default:
      return null;
  }
}

/** Which player an event is about, for player-scoped conditions ("at the end of your turn"). */
export function eventPlayer(event: GameEvent): PlayerIndex | null {
  return 'player' in event ? event.player : null;
}

// ---------------------------------------------------------------------------
// Bag resolution order (CR §7.7.4)
// ---------------------------------------------------------------------------

/** The waiting items this player controls, oldest first — the options they get to order. */
export function bagItemsFor(state: GameState, player: PlayerIndex): BagItem[] {
  return state.bag.items.filter((item) => item.controller === player);
}

/**
 * Who holds the bag next: starting at `from`, the first player in turn order with a waiting
 * trigger. Passing `from = state.activePlayer` gives the active player the first say
 * (CR §7.7.4); passing the current holder keeps the bag with them while they still have items,
 * so a trigger they add mid-resolution is theirs to resolve next (CR §7.7.5).
 *
 * `null` only when the bag is empty.
 */
export function nextBagHolder(state: GameState, from: PlayerIndex): PlayerIndex | null {
  for (const player of turnOrderFrom(state, from)) {
    if (state.bag.items.some((item) => item.controller === player)) return player;
  }
  return null;
}

/** Take one item out of the bag by id. Throws if it is not there. */
export function takeBagItem(state: GameState, id: number): BagItem {
  const at = state.bag.items.findIndex((item) => item.id === id);
  const item = state.bag.items[at];
  if (at === -1 || item === undefined) throw new Error(`Bag item ${id} is not in the bag`);
  state.bag.items.splice(at, 1);
  return item;
}

// ---------------------------------------------------------------------------
// Cloning (SPEC.md §4.2 — every mutable part of the state is copied)
// ---------------------------------------------------------------------------

export function cloneBagState(bag: BagState): BagState {
  return {
    triggers: bag.triggers.map(cloneTriggerSource),
    items: bag.items.map(cloneBagItem),
    nextId: bag.nextId,
    eventCursor: bag.eventCursor,
  };
}

export function cloneTriggerSource(trigger: TriggerSource): TriggerSource {
  return {
    id: trigger.id,
    source: trigger.source,
    when: cloneIr(trigger.when),
    effect: cloneIr(trigger.effect) as Effect[],
    label: trigger.label,
    ...(trigger.expires === undefined ? {} : { expires: trigger.expires }),
    created: trigger.created,
  };
}

/**
 * Copy one ability instance.
 *
 * A `BagItem` *is* an `AbilityInstance` — a trigger taken out of the bag rides on the
 * resolution stack in a `resolve-ability` frame, where the frame's field is typed as the
 * base. So the copy has to carry `trigger` through when it is there: dropping it made a
 * clone structurally different from the state it was cloned from, which ticket 15's
 * clone-equality fuzz invariant is exactly what caught.
 */
export function cloneAbilityInstance(item: AbilityInstance): AbilityInstance {
  const trigger = (item as Partial<BagItem>).trigger;
  return {
    id: item.id,
    controller: item.controller,
    source: item.source,
    subject: item.subject,
    ...(item.eventSource === undefined ? {} : { eventSource: item.eventSource }),
    ...(item.eventAmount === undefined ? {} : { eventAmount: item.eventAmount }),
    ...(item.eventDefender === undefined ? {} : { eventDefender: item.eventDefender }),
    effect: cloneIr(item.effect) as Effect[],
    label: item.label,
    origin: item.origin,
    ...(item.bindings === undefined
      ? {}
      : {
          bindings: item.bindings.map((each) => ({ id: each.id, instances: [...each.instances] })),
        }),
    ...(item.challenge === undefined ? {} : { challenge: { ...item.challenge } }),
    ...(item.finishTo === undefined ? {} : { finishTo: item.finishTo }),
    ...(trigger === undefined ? {} : { trigger }),
  };
}

export function cloneBagItem(item: BagItem): BagItem {
  return { ...cloneAbilityInstance(item), trigger: item.trigger, origin: 'triggered' };
}
